import { useState, useEffect } from 'react';
import { Input, Toggle, FormField, Button, Badge, phoneLengthError } from '@bbpos/ui';
import { RoleSelector } from './RoleSelector';
import type { User, TenantRole } from '@/types/user';
import { useTenantDial } from '@/hooks/use-tenant-dial';

const PANEL_CLASS =
  'rounded-2xl border border-slate-200/80 bg-white shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)]';

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

interface UserProfileTabProps {
  user: User;
  businessType: 'RETAIL' | 'RESTAURANT';
  currentRoleLevel: number;
  onSave: (data: { name?: string; role?: TenantRole; phone?: string; is_active?: boolean }) => void;
  saving?: boolean;
}

const E164_REGEX = /^\+[1-9]\d{6,14}$/;

export function UserProfileTab({ user, businessType, currentRoleLevel, onSave, saving }: UserProfileTabProps) {
  const dial = useTenantDial();
  const [name, setName] = useState(user.name);
  const [phone, setPhone] = useState(user.phone ?? '');
  const [role, setRole] = useState<TenantRole>(user.role as TenantRole);
  const [isActive, setIsActive] = useState(user.is_active);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Sync state when user prop changes
  useEffect(() => {
    setName(user.name);
    setPhone(user.phone ?? '');
    setRole(user.role as TenantRole);
    setIsActive(user.is_active);
    setErrors({});
  }, [user.id, user.name, user.phone, user.role, user.is_active]);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!name || name.length < 2) errs.name = 'Name must be at least 2 characters';
    if (name.length > 200) errs.name = 'Name must be at most 200 characters';
    if (phone && !E164_REGEX.test(phone)) errs.phone = 'Enter a valid phone number';
    else if (phoneLengthError(phone)) errs.phone = phoneLengthError(phone)!;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSave() {
    if (!validate()) return;
    const changes: Record<string, unknown> = {};
    if (name !== user.name) changes.name = name;
    if (role !== user.role) changes.role = role;
    if (phone !== (user.phone ?? '')) changes.phone = phone || undefined;
    if (isActive !== user.is_active) changes.is_active = isActive;
    onSave(changes as { name?: string; role?: TenantRole; phone?: string; is_active?: boolean });
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Edit Form */}
      <div className={cn(PANEL_CLASS, 'lg:col-span-3')}>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-slate-900">Profile Details</h3>
          </div>

          <div className="space-y-4">
            <FormField label="Full Name" htmlFor="user-name" required error={errors.name}>
              <Input
                id="user-name"
                value={name}
                onChange={setName}
              />
            </FormField>

            <FormField label="Email" htmlFor="user-email" helperText="Email cannot be changed after creation">
              <div className="flex items-center gap-2">
                <Input id="user-email" value={user.email} disabled />
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-label="Email is locked and cannot be changed"
                    role="img"
                  >
                    <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>
            </FormField>

            <FormField label="Phone" htmlFor="user-phone" error={errors.phone} helperText="Pick the country code, then enter the mobile number">
              <Input
                id="user-phone"
                variant="phone"
                defaultDial={dial}
                value={phone}
                onChange={setPhone}
              />
            </FormField>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Role
              </label>
              <RoleSelector
                value={role}
                onChange={setRole}
                businessType={businessType}
                currentRoleLevel={currentRoleLevel}
                disabled={saving}
                mode="edit"
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <div>
                <p className="text-sm font-medium text-slate-700">Active Status</p>
                <p className="text-xs text-slate-400">Deactivating logs out the user immediately</p>
              </div>
              <Toggle
                checked={isActive}
                onChange={setIsActive}
                label="User active status"
              />
            </div>

            <div className="pt-4 border-t border-slate-100">
              <div className="flex items-center gap-3">
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={saving}
                  data-save-profile
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <span className="text-xs text-slate-400 hidden sm:inline">Ctrl+S</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Account Info Sidebar */}
      <div className="lg:col-span-2 space-y-4">
        {/* MFA Status */}
        <div className={cn(PANEL_CLASS, 'relative overflow-hidden')}>
          <div className={cn(
            'absolute inset-x-0 top-0 h-12 bg-gradient-to-b',
            user.mfa_enabled ? 'from-emerald-500/20 to-emerald-500/0' : 'from-rose-500/18 to-rose-500/0',
          )} />
          <div className="relative p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className={cn(
                'flex h-9 w-9 items-center justify-center rounded-xl',
                user.mfa_enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600',
              )}>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
              </div>
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">MFA Status</span>
            </div>
            <Badge variant={user.mfa_enabled ? 'success' : 'danger'}>
              {user.mfa_enabled ? 'Enabled' : 'Not Set Up'}
            </Badge>
            {!user.mfa_enabled && (
              <p className="mt-2 text-xs text-slate-500">
                Recommended for accounts with elevated privileges
              </p>
            )}
          </div>
        </div>

        {/* Last Login */}
        <div className={cn(PANEL_CLASS, 'relative overflow-hidden')}>
          <div className="absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-sky-500/18 to-sky-500/0" />
          <div className="relative p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Last Login</span>
            </div>
            <p className="text-sm font-semibold text-slate-900 tabular-nums">
              {user.last_login_at
                ? new Date(user.last_login_at).toLocaleString('en-IN')
                : 'Never logged in'}
            </p>
          </div>
        </div>

        {/* Created */}
        <div className={cn(PANEL_CLASS, 'relative overflow-hidden')}>
          <div className="absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-indigo-500/18 to-indigo-500/0" />
          <div className="relative p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                </svg>
              </div>
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Account Created</span>
            </div>
            <p className="text-sm font-semibold text-slate-900">
              {new Date(user.created_at).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
